import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { CloudUpload, Clear, Search, ImageOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import clinicService from '../../../../services/clinic.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import useEmailEditorStore from '../utilities/emailEditorStore';
import { BLOCK_TYPES } from '../utilities/blockTypes';
import '../../../articles/article-media-picker.css';

function isImageResource(item) {
  return item?.type?.startsWith('image/');
}

function isImageFile(file) {
  return file?.type?.startsWith('image/');
}

function EmailEditorMediaLibrary({ onSelectPreviewImage, previewImagePath }) {
  const clinicId = useEyeconxStore((state) => state.clinicId);
  const {
    selectedBlockId,
    getSelectedBlock,
    addBlock,
    updateBlockProperties,
  } = useEmailEditorStore();

  const uploadInputRef = useRef(null);
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const loadMedia = useCallback(() => {
    if (!clinicId) {
      setError('Admin clinic is not available.');
      setMediaList([]);
      return Promise.resolve();
    }

    setLoading(true);
    setError(null);

    return clinicService
      .getMedia(clinicId)
      .then((res) => {
        setMediaList((res.data || []).filter(isImageResource));
      })
      .catch(() => {
        setError('Failed to load media library images.');
        setMediaList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clinicId]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const filteredMedia = useMemo(() => {
    if (!searchTerm) {
      return mediaList;
    }

    const term = searchTerm.toLowerCase();
    return mediaList.filter((item) => item.name?.toLowerCase().includes(term));
  }, [mediaList, searchTerm]);

  const applyImage = useCallback(
    (imagePath, item) => {
      const selectedBlock = getSelectedBlock();
      const imageProps = {
        src: imagePath,
        ...(item?.name ? { alt: item.name } : {}),
      };

      if (selectedBlock?.type === BLOCK_TYPES.IMAGE) {
        updateBlockProperties(selectedBlock.id, imageProps);
        return;
      }

      const parentId =
        selectedBlock &&
        [BLOCK_TYPES.CONTAINER, BLOCK_TYPES.COLUMN, BLOCK_TYPES.COLUMNCONTAINER].includes(
          selectedBlock.type
        )
          ? selectedBlock.id
          : null;

      const newBlockId = addBlock(BLOCK_TYPES.IMAGE, parentId);
      updateBlockProperties(newBlockId, imageProps);
    },
    [addBlock, getSelectedBlock, updateBlockProperties]
  );

  const handleUpload = useCallback(
    (file) => {
      if (!file || isUploading) {
        return;
      }

      if (!clinicId) {
        toast.error('Admin clinic is not available.', { position: toast.POSITION.BOTTOM_RIGHT });
        return;
      }

      if (!isImageFile(file)) {
        toast.error('Please upload an image file.', { position: toast.POSITION.BOTTOM_RIGHT });
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);

      clinicService
        .uploadMedia(clinicId, formData, (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(progress);
        })
        .then((res) => {
          toast.success('Image uploaded successfully', { position: toast.POSITION.BOTTOM_RIGHT });

          const uploaded = res.data;
          if (uploaded?.path && isImageResource(uploaded)) {
            applyImage(uploaded.path, uploaded);
            loadMedia();
            return;
          }

          loadMedia();
        })
        .catch(() => {
          toast.error('Upload failed. Please try again.', { position: toast.POSITION.BOTTOM_RIGHT });
        })
        .finally(() => {
          setIsUploading(false);
          setUploadProgress(0);
          if (uploadInputRef.current) {
            uploadInputRef.current.value = '';
          }
        });
    },
    [applyImage, clinicId, isUploading, loadMedia]
  );

  const selectedBlock = getSelectedBlock();
  const hint =
    selectedBlock?.type === BLOCK_TYPES.IMAGE
      ? 'Click an image to apply it to the selected image block.'
      : 'Click an image to insert a new image block.';

  const handleSetPreview = useCallback(
    (event, item) => {
      event.stopPropagation();
      if (!onSelectPreviewImage) {
        return;
      }
      onSelectPreviewImage(item.path, item);
      toast.success('Preview image path updated.', { position: toast.POSITION.BOTTOM_RIGHT });
    },
    [onSelectPreviewImage]
  );

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        3ConX Media Library
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {hint}
      </Typography>
      {onSelectPreviewImage && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use the image icon on a file to set it as the template Preview Image Path.
        </Typography>
      )}

      {previewImagePath ? (
        <Box
          sx={{
            mb: 2,
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'rgba(0, 0, 0, .02)',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Current template preview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={previewImagePath}
              alt="Template preview"
              sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 0.5, bgcolor: '#fff' }}
            />
            <Typography
              variant="caption"
              sx={{
                flex: 1,
                wordBreak: 'break-all',
                color: 'text.secondary',
              }}
            >
              {previewImagePath}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <Box
        className={`article-media-picker__upload${isDragging ? ' article-media-picker__upload--dragging' : ''}${
          isUploading ? ' article-media-picker__upload--uploading' : ''
        }`}
        onClick={() => !isUploading && uploadInputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          if (event.dataTransfer.files.length > 0) {
            handleUpload(event.dataTransfer.files[0]);
          }
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !isUploading) {
            uploadInputRef.current?.click();
          }
        }}
        sx={{ mb: 2 }}
      >
        <input
          ref={uploadInputRef}
          type="file"
          className="d-none"
          accept="image/*"
          onChange={(event) => handleUpload(event.target.files?.[0])}
          disabled={isUploading || !clinicId}
        />
        <CloudUpload sx={{ color: '#4154f1', mb: 0.5 }} />
        <div className="article-media-picker__upload-title">
          {isDragging ? 'Drop image here' : 'Upload image to admin library'}
        </div>
        <p className="article-media-picker__upload-hint mb-0">
          Drag and drop or click to browse.
        </p>
        {isUploading && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Uploading: {uploadProgress}%
            </Typography>
          </Box>
        )}
      </Box>

      <TextField
        size="small"
        fullWidth
        placeholder="Search images..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {filteredMedia.length} image{filteredMedia.length !== 1 ? 's' : ''}
          {selectedBlockId ? ' · block selected' : ''}
        </Typography>
        <Button size="small" onClick={loadMedia} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : filteredMedia.length > 0 ? (
        <Box
          className="article-media-picker__grid"
          sx={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr)) !important',
            maxHeight: '55vh',
            overflowY: 'auto',
            pr: 0.5,
          }}
        >
          {filteredMedia.map((item) => {
            const isPreview = Boolean(previewImagePath && previewImagePath === item.path);
            return (
              <Box
                key={item.id}
                className="article-media-picker__item"
                title={item.name}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  border: isPreview ? '2px solid #4154f1' : '1px solid rgba(1, 41, 112, 0.12)',
                  background: '#fff',
                  borderRadius: 1,
                  p: 0.75,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => applyImage(item.path, item)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    border: 0,
                    background: 'transparent',
                    p: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div className="article-media-picker__thumb" style={{ height: 72 }}>
                    <img src={item.path} alt={item.name} loading="lazy" />
                  </div>
                  <span className="article-media-picker__name">{item.name}</span>
                </Box>

                {onSelectPreviewImage && (
                  <IconButton
                    size="small"
                    onClick={(event) => handleSetPreview(event, item)}
                    title="Set as template preview"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: isPreview ? '#4154f1' : 'rgba(255,255,255,0.95)',
                      color: isPreview ? '#fff' : 'inherit',
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: isPreview ? '#3141c5' : '#fff',
                      },
                    }}
                  >
                    <ImageOutlined fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <div className="article-media-picker__empty">
          <i className="bi bi-images"></i>
          <p className="mb-0">
            {searchTerm
              ? 'No images match your search.'
              : 'No images in the admin library yet. Upload one above.'}
          </p>
        </div>
      )}
    </Box>
  );
}

export default EmailEditorMediaLibrary;
